<template>
  <b-modal
    id="modal-driver-document"
    :title="$t('fleetTaskInstant.field.driverDocument')"
    size="lg"
    centered
    scrollable
    noCloseOnEsc
    noCloseOnBackdrop
  >
    <p class="__driver-document-title">
      {{ $t('fleetTaskInstant.field.driverDocument') }}
    </p>

    <div v-if="computedAttachments.length > 0" class="__driver-attachments">
      <div v-for="(attachment, idx) in computedAttachments" :key="attachment.id" class="attachment">
        <div
          class="attachment__wrapper"
          :class="
            attachment.type === 'FLEET_TASK_INSTANT_DRIVER_ATTACHMENT_PHOTO' ? '__photo' : '__file'
          "
          @click="activeAttachmentIdx = idx"
        >
          <img
            v-if="attachment.name.endsWith('pdf')"
            src="@/assets/images/download-format/pdf.png"
            alt
          />

          <img
            v-else-if="attachment.name.endsWith('xlsx')"
            src="@/assets/images/download-format/excel.png"
            alt
          />

          <img v-else :src="attachment.url" alt />

          <button
            class="__delete-btn"
            :disabled="isLoading"
            @click.stop="handleDeleteAttachment(attachment.id)"
          >
            <i class="icon-outline_trash"></i>
          </button>
        </div>

        <p class="attachment__name">
          {{ attachment.name }}
        </p>
      </div>
    </div>

    <McButton
      icon="icon-outline_upload"
      :text="$t('fleetTaskInstant.action.uploadDocument')"
      :disabled="isLoading || computedAttachments.length >= 10"
      @click="$refs.fileInputRef.click()"
    />

    <input
      ref="fileInputRef"
      type="file"
      multiple
      hidden
      :accept="fileAccepted.join(', ')"
      @change="handleAddDocuments"
    />

    <PreviewImage
      v-if="activeAttachmentIdx !== null"
      :attachments="computedAttachments"
      :activeAttachmentIdx="activeAttachmentIdx"
      :customHandlers="customHandlers"
      @update:activeAttachmentIdx="activeAttachmentIdx = $event"
    />

    <template #modal-footer="{ cancel }">
      <b-button size="md" variant="outline-primary" @click="cancel()">
        {{ $t('global.action.close') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import Compressor from 'compressorjs'

import McButton from '@/components/McButton'
import PreviewImage from '../../fleet-task-instant/components/PreviewImage.vue'

import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import uploadStorage from '@/services/rest-api/lib/fleet-task-instant/storage'

export default {
  components: {
    McButton,
    PreviewImage,
  },
  props: {
    foId: {
      type: String,
      default: null,
    },
    attachments: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['refetch-list-fo'],
  data() {
    return {
      isLoading: false,
      activeAttachmentIdx: null,
      customHandlers: {
        handleDownloadImg: async (imgUrl, fileName, file) => {
          const res = await fetch(imgUrl, {
            headers: new Headers({
              'Cache-Control': 'no-cache',
            }),
          })
          if (!res) return

          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          Object.assign(document.createElement('a'), { href: url, download: file.name }).click()
          URL.revokeObjectURL(url)
        },

        handlePathUrl: (file) => {
          return file.url
        },
      },
    }
  },
  computed: {
    computedAttachments() {
      if (!this.attachments?.photos && !this.attachments?.documents) {
        return []
      }

      const attachments = []

      this.attachments?.photos?.forEach((item) => {
        attachments.push({
          id: item.id,
          name: item.file_name,
          url: item.url,
          type: item.type,
        })
      })

      this.attachments?.documents?.forEach((item) => {
        attachments.push({
          id: item.id,
          name: item.file_name,
          url: item.url,
          type: item.type,
        })
      })

      return attachments
    },

    attachmentIds() {
      return this.computedAttachments?.map?.((item) => item.id)
    },

    fileAccepted() {
      return ['.png', '.jpg', '.jpeg', '.pdf', '.xlsx']
    },
  },
  methods: {
    async handleAddDocuments(e) {
      try {
        const files = e.target.files

        // Validate file counts
        if (files.length + this.attachments.length > this.maxImageFiles) {
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTaskInstant.toast.driverAttachment.exceedMaxDoc'),
                },
              ],
            },
          })
          return
        }

        const attachments = []
        for (let i = 0; i < files.length; i++) {
          const file = files[i]

          // Validate file type
          const fileTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'image/png',
            'image/jpg',
            'image/jpeg',
            'application/pdf',
          ]
          if (!fileTypes.includes(file.type)) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.driverAttachment.invalidExt'),
                  },
                ],
              },
            })
            return
          }

          // Validate file size
          if (file.size > this.maxFileSize) {
            this.$mcToast.error({
              component: 'McToastDynamicBody',
              props: {
                contents: [
                  {
                    html: this.$t('fleetTaskInstant.toast.driverAttachment.exceedMaxDocSize'),
                  },
                ],
              },
            })
            return
          }

          let processedFile = file

          if (['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
            processedFile = await new Promise((resolve, reject) => {
              new Compressor(file, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.8,
                success: resolve,
                error: reject,
              })
            })
          }

          attachments.push(processedFile)
        }

        await this.handleUploadDocuments(attachments)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error selecting files:', error)
          this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        }
      } finally {
        this.$refs.fileInputRef.value = null
      }
    },

    async handleUploadDocuments(attachments) {
      this.isLoading = true

      const promises = []

      attachments.forEach((item) => {
        promises.push(uploadStorage(item))
      })

      const responses = await Promise.all(promises)

      const ids = [...this.attachmentIds]
      let isError = false

      responses.forEach((res) => {
        if (res.errors) {
          isError = true
          return
        }

        ids.push(res.data.id)
      })

      if (isError) {
        this.isLoading = false
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        return
      }

      const resUpload = await FleetTaskInstant.uploadDriverAttachments(this.foId, {
        driver_attachment_file_ids: ids,
      })

      if (resUpload.errors) {
        this.isLoading = false
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedUpload'))
        return
      }

      this.$emit('refetch-list-fo')
      this.isLoading = false
      this.$mcToast.success(this.$t('fleetTaskInstant.toast.driverAttachment.successUpload'))
    },

    async handleDeleteAttachment(id) {
      this.isLoading = true

      const ids = this.attachmentIds.filter((item) => item !== id)

      const resUpload = await FleetTaskInstant.uploadDriverAttachments(this.foId, {
        driver_attachment_file_ids: ids,
      })

      if (resUpload.errors) {
        this.isLoading = false
        this.$mcToast.error(this.$t('fleetTaskInstant.toast.driverAttachment.failedDelete'))
        return
      }

      this.$emit('refetch-list-fo')
      this.isLoading = false
      this.$mcToast.success(this.$t('fleetTaskInstant.toast.driverAttachment.successDelete'))
    },
  },
}
</script>

<style lang="scss" scoped>
.__driver-attachments {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment {
  position: relative;
  max-width: 80px;

  &__wrapper {
    width: 80px;
    height: 80px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;

    img {
      object-fit: cover;
    }

    &.__photo {
      img {
        width: 100%;
        height: 100%;
      }
    }

    &.__file {
      border: 1px dashed $mep-color-text-300;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  &__name {
    margin-top: 8px;
    font-size: 0.8em;
    font-weight: 600;
    text-align: center;

    width: 80px;
    overflow: hidden;

    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;

    color: $primary;
  }

  .__delete-btn {
    position: absolute;
    top: 3px;
    right: 3px;
    height: 18px;
    width: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid $red;
    border-radius: 3px;
    background-color: $white;

    i::before {
      font-size: 13px;
      color: $red;
    }
  }

  .__delete-btn:hover {
    background-color: $red;

    i::before {
      color: $white;
    }
  }
}
</style>
